import React, { useState, useRef, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { FileText, Upload, X, Download, Trash2, Plus, Edit, Sparkles, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import jsPDF from 'jspdf';
import SignatureCanvas from 'react-signature-canvas';
import { offlineSend } from '../lib/offlineQueue';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Logo Main Solution in base64 (verrà caricato dinamicamente)
const LOGO_URL = '/logo.png';

const VerbaleFormDialog = ({ 
  open, 
  onOpenChange, 
  cantiere, 
  cantiereId,
  users = [],
  tipoVerbale = 'sopralluogo',
  onSave,
  onPdfSaved,
  onPdfGenerated,
  figureProfessionaliCantiere = [],
  codiceProgettoMS = '',
  codiceProgettoCliente = '',
  sopralluoghiEsistenti = [],
  sopralluogoEsistente = null,  // Dati di un sopralluogo già salvato per generare il PDF
  isPdfEnabled = false  // Prop dal padre per controllare abilitazione PDF
}) => {
  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  // Refs per le firme
  const sigCanvasCSE = useRef(null);
  const sigCanvasDL = useRef(null);
  const sigCanvasSito = useRef(null);
  const sigCanvasCommittente = useRef(null);
  const sigCanvasImpresa = useRef(null);
  const sigCanvasPreposto = useRef(null);

  const [formData, setFormData] = useState({
    tipo: tipoVerbale,
    tipo_custom: '', // Testo personalizzato quando si seleziona "altro" come tipo
    numero_verbale: '',
    data_verbale: new Date().toISOString().split('T')[0],
    ora_inizio: '',
    ora_fine: '',
    durata_ore: '',
    cantiere_nome: '',
    cantiere_indirizzo: '',
    pm_cliente: '', // Rinominato da committente
    impresa_affidataria: '',
    impresa_esecutrice: '',
    impresa_subappaltatrice: '',
    codice_commessa_ms: '',
    codice_commessa_cliente: '',
    // Scelta ruolo firmatario
    ruolo_firmatario: 'direttore_lavori', // 'direttore_lavori' o 'assistente_dl'
    genere_firmatario: 'M', // M o F per la grammatica
    direttore_lavori_id: '',
    direttore_lavori_nome: '',
    direttore_lavori_ordine: '',
    direttore_lavori_indirizzo: '',
    // Testo introduttivo editabile
    testo_introduttivo: '',
    // Tipo firma: tradizionale (carta) o digitale
    tipo_firma: 'tradizionale',
    scopo_riunione: [],
    scopo_altro_testo: '', // Testo personalizzato quando si seleziona "Altro"
    argomenti_discussi: '',
    foto: []
  });
  
  // Tipi verbale predefiniti
  const tipiVerbalePredefiniti = [
    { value: 'sopralluogo', label: 'Sopralluogo' },
    { value: 'riunione', label: 'Riunione di Coordinamento' },
    { value: 'sicurezza', label: 'Riunione Sicurezza' },
    { value: 'progresso', label: 'Riunione Progresso Lavori' },
    { value: 'committente', label: 'Riunione con Committente' },
    { value: 'tecnica', label: 'Riunione Tecnica' },
    { value: 'altro', label: 'Altro (specifica)' }
  ];
  
  // Helper per ottenere il label del tipo verbale (incluso tipo custom)
  const getTipoVerbaleLabel = () => {
    if (formData.tipo === 'altro' && formData.tipo_custom) {
      return formData.tipo_custom;
    }
    const tipoPredefinito = tipiVerbalePredefiniti.find(t => t.value === formData.tipo);
    return tipoPredefinito ? tipoPredefinito.label : formData.tipo;
  };

  // State per righe partecipanti dinamiche
  const [partecipantiRows, setPartecipantiRows] = useState([
    { id: 1, categoria: 'impresa_affidataria', nome: '' }
  ]);

  const [fotoPreview, setFotoPreview] = useState([]);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [logoBase64, setLogoBase64] = useState(null);
  // Usa isPdfEnabled dal parent come fonte di verità, con fallback locale
  const [localSaved, setLocalSaved] = useState(false);
  const canGeneratePdf = isPdfEnabled || localSaved || !!sopralluogoEsistente;
  
  // State per file verbale firmato da caricare
  const [verbaleFirmatoFile, setVerbaleFirmatoFile] = useState(null);
  const verbaleFirmatoInputRef = useRef(null);
  
  // State per i dettagli di ogni scopo selezionato (testo + foto)
  const [scopiDettagli, setScopiDettagli] = useState({});
  
  // State per input manuale figure
  const [showManualInput, setShowManualInput] = useState({
    impresa_affidataria: false,
    impresa_esecutrice: false,
    impresa_subappaltatrice: false,
    adl: false
  });
  
  // State per AI
  const [aiLoading, setAiLoading] = useState({});
  
  // Funzione per migliorare testo con AI
  const improveTextWithAI = async (fieldName, text, context = 'verbale di cantiere') => {
    if (!text || text.trim().length < 10) {
      toast.error('Inserisci almeno 10 caratteri prima di usare l\'AI');
      return;
    }
    
    setAiLoading(prev => ({ ...prev, [fieldName]: true }));
    
    try {
      const response = await axios.post(`${API}/ai/improve-text`, {
        text,
        context,
        action: 'improve'
      }, getAuthHeaders());
      
      if (response.data.success) {
        // Aggiorna il campo appropriato
        if (fieldName === 'testo_introduttivo') {
          setFormData(prev => ({ ...prev, testo_introduttivo: response.data.improved }));
        } else if (fieldName.startsWith('scopo_')) {
          const scopoName = fieldName.replace('scopo_', '');
          setScopiDettagli(prev => ({
            ...prev,
            [scopoName]: {
              ...prev[scopoName],
              descrizione: response.data.improved
            }
          }));
        }
        toast.success('Testo migliorato con AI');
      }
    } catch (error) {
      console.error('Errore AI:', error);
      toast.error('Errore nel miglioramento del testo');
    } finally {
      setAiLoading(prev => ({ ...prev, [fieldName]: false }));
    }
  };

  // Carica logo come base64
  useEffect(() => {
    const loadLogo = async () => {
      try {
        const response = await fetch(LOGO_URL);
        const blob = await response.blob();
        const reader = new FileReader();
        reader.onloadend = () => {
          setLogoBase64(reader.result);
        };
        reader.readAsDataURL(blob);
      } catch (error) {
        console.error('Errore caricamento logo:', error);
      }
    };
    loadLogo();
  }, []);

  // Pre-popola i dati dal sopralluogo esistente quando il dialog si apre
  useEffect(() => {
    if (open && sopralluogoEsistente) {
      // Pre-popola i dati dal sopralluogo esistente
      setFormData(prev => ({
        ...prev,
        tipo: 'sopralluogo',
        data_verbale: sopralluogoEsistente.data_sopralluogo ? sopralluogoEsistente.data_sopralluogo.split('T')[0] : prev.data_verbale,
        durata_ore: sopralluogoEsistente.durata_ore || '',
        numero_verbale: sopralluogoEsistente.descrizione?.match(/Verbale N\.(\d+)/)?.[1] || prev.numero_verbale
      }));
    }
    // Reset localSaved solo quando il dialog viene chiuso
    if (!open) {
      setLocalSaved(false);
    }
  }, [open, sopralluogoEsistente]);

  // Aggiorna il tipo del verbale quando il prop tipoVerbale cambia
  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      tipo: tipoVerbale
    }));
  }, [tipoVerbale]);


  const scopiPredefiniti = [
    'Verifica dello stato di applicazione del progetto',
    'Verifica del cronoprogramma dei lavori',
    'Programmazione di eventuali modifiche tecniche progettuali',
    'Verifica delle condizioni di sicurezza',
    'Coordinamento delle imprese presenti',
    'Altro'
  ];

  // Calcola il prossimo numero verbale progressivo
  const calcolaNumeroVerbaleProgressivo = () => {
    if (!sopralluoghiEsistenti || sopralluoghiEsistenti.length === 0) {
      return '1';
    }
    // Filtra per tipo verbale (sopralluogo o riunione)
    const verbaliStessoTipo = sopralluoghiEsistenti.filter(s => {
      if (tipoVerbale === 'sopralluogo') {
        return s.tipo_attivita !== 'Verbale Riunione di Coordinamento';
      } else {
        return s.tipo_attivita === 'Verbale Riunione di Coordinamento';
      }
    });
    return String(verbaliStessoTipo.length + 1);
  };
  
  // Trova l'ultimo verbale per pre-popolare i partecipanti
  const getUltimoVerbale = () => {
    if (!sopralluoghiEsistenti || sopralluoghiEsistenti.length === 0) {
      return null;
    }
    // Ordina per data decrescente
    const verbaliOrdinati = [...sopralluoghiEsistenti].sort((a, b) => {
      const dataA = new Date(a.data_sopralluogo || a.created_at);
      const dataB = new Date(b.data_sopralluogo || b.created_at);
      return dataB - dataA;
    });
    return verbaliOrdinati[0];
  };

  useEffect(() => {
    if (cantiere) {
      // Calcola numero verbale progressivo
      const numeroProgressivo = calcolaNumeroVerbaleProgressivo();
      
      setFormData(prev => ({
        ...prev,
        tipo: tipoVerbale, // Aggiorna il tipo quando il prop cambia
        numero_verbale: sopralluogoEsistente && prev.numero_verbale ? prev.numero_verbale : numeroProgressivo,
        cantiere_nome: cantiere.nome_progetto || cantiere.nome || '',
        cantiere_indirizzo: cantiere.indirizzo || '',
        pm_cliente: cantiere.pm_cliente || cantiere.committente || '',
        impresa_affidataria: cantiere.impresa_affidataria || '',
        codice_commessa_ms: codiceProgettoMS || cantiere.codice_progetto_main_solution || '',
        codice_commessa_cliente: codiceProgettoCliente || cantiere.codice_progetto_cliente || ''
      }));
    }
  }, [cantiere, codiceProgettoMS, codiceProgettoCliente, sopralluoghiEsistenti, tipoVerbale]);
  
  // Genera testo introduttivo dinamico basato sui campi Responsabili
  useEffect(() => {
    const dataOggi = formData.data_verbale ? new Date(formData.data_verbale).toLocaleDateString('it-IT') : new Date().toLocaleDateString('it-IT');
    const nome = formData.direttore_lavori_nome || '[Nome Cognome]';
    const ordine = formData.direttore_lavori_ordine ? ` iscritto all'Ordine degli Ingegneri con il n°${formData.direttore_lavori_ordine}` : '';
    
    let ruoloTesto = '';
    let sottoscritto = formData.genere_firmatario === 'F' ? 'La sottoscritta' : 'Il sottoscritto';
    let titolo = formData.genere_firmatario === 'F' ? 'Ing.' : 'Ing.';
    
    switch (formData.ruolo_firmatario) {
      case 'direttore_lavori':
        ruoloTesto = 'in qualità di Direttore dei Lavori';
        break;
      case 'assistente_dl':
        ruoloTesto = 'in qualità di Assistente del Direttore dei Lavori';
        break;
      case 'assistente_cantiere':
        ruoloTesto = 'in qualità di Assistente di Cantiere';
        break;
      case 'cse':
        ruoloTesto = 'in qualità di Coordinatore per la Sicurezza in fase di Esecuzione (CSE)';
        break;
      case 'csp':
        ruoloTesto = 'in qualità di Coordinatore per la Sicurezza in fase di Progettazione (CSP)';
        break;
      default:
        ruoloTesto = 'in qualità di Direttore dei Lavori';
    }
    
    const testoIntro = `${sottoscritto} ${titolo} ${nome} ${ruoloTesto},${ordine} e domiciliato presso il suo studio, per il cantiere di cui all'oggetto in data ${dataOggi} alla presenza di:`;
    
    setFormData(prev => ({ ...prev, testo_introduttivo: testoIntro }));
  }, [formData.ruolo_firmatario, formData.genere_firmatario, formData.direttore_lavori_nome, formData.direttore_lavori_ordine, formData.data_verbale]);
  
  // Calcolo automatico della durata quando cambiano ora_inizio o ora_fine
  useEffect(() => {
    if (formData.ora_inizio && formData.ora_fine) {
      const [oreInizio, minutiInizio] = formData.ora_inizio.split(':').map(Number);
      const [oreFine, minutiFine] = formData.ora_fine.split(':').map(Number);
      
      const totMinutiInizio = oreInizio * 60 + minutiInizio;
      const totMinutiFine = oreFine * 60 + minutiFine;
      
      if (totMinutiFine > totMinutiInizio) {
        const differenzaMinuti = totMinutiFine - totMinutiInizio;
        const ore = differenzaMinuti / 60;
        // Arrotonda a 2 decimali (es: 2.5 ore)
        const durataFormattata = Math.round(ore * 100) / 100;
        setFormData(prev => ({ ...prev, durata_ore: String(durataFormattata) }));
      }
    }
  }, [formData.ora_inizio, formData.ora_fine]);
  
  // Categorie partecipanti per i dropdown con mapping ai ruoli delle figure professionali
  const categoriePartecipanti = [
    { value: 'assistente_cantiere', label: 'Assistente di cantiere', ruoloFigura: 'assistente_cantiere' },
    { value: 'project_manager', label: 'Project Manager', ruoloFigura: 'pm' },
    { value: 'cse', label: 'Coordinatore per la fase di esecuzione', ruoloFigura: 'cse' },
    { value: 'responsabile_lavori', label: 'Responsabile dei Lavori', ruoloFigura: 'rl' },
    { value: 'impresa_affidataria', label: "Per l'Impresa Affidataria", ruoloFigura: null },
    { value: 'impresa_esecutrice', label: "Per l'Impresa Esecutrice", ruoloFigura: null },
    { value: 'impresa_subappaltatrice', label: "Per l'Impresa Subappaltatrice", ruoloFigura: null },
    { value: 'adl', label: 'Per ADL', ruoloFigura: 'dl' },
    { value: 'committente', label: 'Per il Committente', ruoloFigura: null },
    { value: 'hse', label: 'Per HSE', ruoloFigura: 'hse' }
  ];
  
  // Funzione per auto-compilare il nome in base alla categoria selezionata
  const getAutoFillNameForCategory = (categoriaValue) => {
    const categoria = categoriePartecipanti.find(c => c.value === categoriaValue);
    if (!categoria || !categoria.ruoloFigura) return '';
    
    // Cerca prima nelle figure professionali del cantiere
    const figura = figureProfessionaliCantiere.find(f => 
      f.ruolo?.toLowerCase() === categoria.ruoloFigura.toLowerCase()
    );
    if (figura?.nome_completo) return figura.nome_completo;
    
    // Cerca anche nei campi formData già compilati (Responsabili)
    if (categoria.ruoloFigura === 'dl' && formData.direttore_lavori_nome) return formData.direttore_lavori_nome;
    
    return '';
  };
  
  // Funzioni per gestire righe partecipanti dinamiche
  const addPartecipanteRow = () => {
    const newId = Math.max(...partecipantiRows.map(r => r.id), 0) + 1;
    setPartecipantiRows([...partecipantiRows, { id: newId, categoria: '', nome: '' }]);
  };
  
  const removePartecipanteRow = (id) => {
    if (partecipantiRows.length > 1) {
      setPartecipantiRows(partecipantiRows.filter(r => r.id !== id));
    }
  };
  
  const updatePartecipanteRow = (id, field, value) => {
    setPartecipantiRows(partecipantiRows.map(r => {
      if (r.id !== id) return r;
      
      // Se stiamo cambiando la categoria, proviamo ad auto-compilare il nome
      if (field === 'categoria') {
        const autoNome = getAutoFillNameForCategory(value);
        return { ...r, categoria: value, nome: autoNome || r.nome };
      }
      
      return { ...r, [field]: value };
    }));
  };
  
  // Funzione per selezionare figura dal dropdown e aggiungerla alla riga
  const selectFiguraForRow = (rowId, figura) => {
    if (figura === 'manual') {
      // Focus sull'input per inserimento manuale
      return;
    }
    updatePartecipanteRow(rowId, 'nome', figura);
  };
  
  // Funzione per ottenere le figure professionali per ruolo
  const getFigureByRuolo = (ruolo) => {
    return figureProfessionaliCantiere.filter(f => f.ruolo === ruolo);
  };
  
  // Funzione per selezionare una figura dal dropdown
  const handleSelectFigura = (campo, figura) => {
    if (figura === 'manual') {
      setShowManualInput(prev => ({ ...prev, [campo]: true }));
    } else {
      setFormData(prev => ({
        ...prev,
        [campo]: prev[campo] ? `${prev[campo]}, ${figura}` : figura
      }));
    }
  };
  
  // Funzione per aggiungere nome manuale
  const addManualName = (campo, nome) => {
    if (nome.trim()) {
      setFormData(prev => ({
        ...prev,
        [campo]: prev[campo] ? `${prev[campo]}, ${nome.trim()}` : nome.trim()
      }));
    }
    setShowManualInput(prev => ({ ...prev, [campo.replace('partecipanti_', '')]: false }));
  };

  const handleFotoUpload = (e) => {
    const files = Array.from(e.target.files);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFotoPreview(prev => [...prev, {
          file,
          preview: reader.result,
          descrizione: ''
        }]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removeFoto = (index) => {
    setFotoPreview(prev => prev.filter((_, i) => i !== index));
  };

  const updateFotoDescrizione = (index, descrizione) => {
    setFotoPreview(prev => prev.map((foto, i) => 
      i === index ? { ...foto, descrizione } : foto
    ));
  };

  const handleScopoChange = (scopo, checked) => {
    setFormData(prev => ({
      ...prev,
      scopo_riunione: checked 
        ? [...prev.scopo_riunione, scopo]
        : prev.scopo_riunione.filter(s => s !== scopo)
    }));
    
    // Se lo scopo viene deselezionato, rimuovi anche i suoi dettagli
    if (!checked) {
      setScopiDettagli(prev => {
        const newDetails = { ...prev };
        delete newDetails[scopo];
        return newDetails;
      });
    } else {
      // Inizializza i dettagli per questo scopo
      setScopiDettagli(prev => ({
        ...prev,
        [scopo]: { descrizione: '', foto: [] }
      }));
    }
  };
  
  // Aggiorna la descrizione di uno scopo specifico
  const updateScopoDescrizione = (scopo, descrizione) => {
    setScopiDettagli(prev => ({
      ...prev,
      [scopo]: { ...prev[scopo], descrizione }
    }));
  };
  
  // Gestione foto per uno scopo specifico
  const handleScopoFotoUpload = (scopo, e) => {
    const files = Array.from(e.target.files);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setScopiDettagli(prev => ({
          ...prev,
          [scopo]: {
            ...prev[scopo],
            foto: [...(prev[scopo]?.foto || []), {
              file,
              preview: reader.result,
              descrizione: ''
            }]
          }
        }));
      };
      reader.readAsDataURL(file);
    });
  };
  
  // Rimuovi foto da uno scopo
  const removeScopoFoto = (scopo, index) => {
    setScopiDettagli(prev => ({
      ...prev,
      [scopo]: {
        ...prev[scopo],
        foto: prev[scopo].foto.filter((_, i) => i !== index)
      }
    }));
  };
  
  // Aggiorna descrizione foto di uno scopo
  const updateScopoFotoDescrizione = (scopo, fotoIndex, descrizione) => {
    setScopiDettagli(prev => ({
      ...prev,
      [scopo]: {
        ...prev[scopo],
        foto: prev[scopo].foto.map((foto, i) => 
          i === fotoIndex ? { ...foto, descrizione } : foto
        )
      }
    }));
  };

  const clearSignature = (ref) => {
    if (ref.current) {
      ref.current.clear();
    }
  };

  // Genera PDF con design professionale - Layout Main Solution
  const generatePDF = async () => {
    setGeneratingPdf(true);
    console.log('[PDF] Iniziando generazione PDF...');
    
    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 20;
      let yPos = margin;
      
      // ========================================
      // COLORI MAIN SOLUTION - Blu professionale
      // ========================================
      const mainBlue = [0, 82, 147];       // #005293 - Blu Main Solution
      const lightBlue = [230, 240, 250];   // Sfondo azzurro chiaro
      const darkText = [40, 40, 40];       // Testo scuro
      const blackText = [0, 0, 0];         // Testo nero per titoli
      const grayText = [100, 100, 100];    // Testo grigio
      const lightGray = [245, 245, 245];   // Sfondo grigio chiaro
      const borderGray = [200, 200, 200];  // Bordi
      
      // ========================================
      // FONT SIZE STANDARD - Ridotti per migliore leggibilità
      // (Calibri Light non supportato da jsPDF, usiamo helvetica normale)
      // ========================================
      const FONT_NORMAL = 10;         // Font standard ridotto (era 12)
      const FONT_CAPTION = 7;         // Font per didascalie foto (era 8)
      const FONT_SMALL = 9;           // Font piccolo per info secondarie (era 10)
      const FONT_TITLE = 12;          // Font titolo principale (era 14)
      
      // ========================================
      // HELPER FUNCTIONS
      // ========================================
      const addHeader = () => {
        // Linea blu sottile in alto
        pdf.setDrawColor(...mainBlue);
        pdf.setLineWidth(0.8);
        pdf.line(margin, 12, pageWidth - margin, 12);
        
        // Logo Main Solution - PROPORZIONI ORIGINALI (261x236 = aspect ratio 1.106)
        // Dimensioni: larghezza 28mm, altezza 28/1.106 = 25.3mm
        const logoWidth = 28;
        const logoHeight = 25.3;  // Mantiene proporzioni originali
        
        if (logoBase64) {
          try {
            // Disegna sfondo bianco dietro il logo per uniformare il colore
            pdf.setFillColor(255, 255, 255);
            pdf.rect(margin - 1, 14, logoWidth + 2, logoHeight + 2, 'F');
            pdf.addImage(logoBase64, 'PNG', margin, 15, logoWidth, logoHeight);
          } catch (e) {
            console.warn('Logo non caricato:', e);
          }
        }
        
        // Info Commessa a destra
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(FONT_CAPTION);
        pdf.setTextColor(...grayText);
        pdf.text('COMMESSA', pageWidth - margin, 22, { align: 'right' });
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(FONT_SMALL);
        pdf.setTextColor(...mainBlue);
        pdf.text(formData.codice_commessa_ms || 'N/D', pageWidth - margin, 28, { align: 'right' });
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(FONT_CAPTION);
        pdf.setTextColor(...grayText);
        pdf.text(formData.codice_commessa_cliente || '', pageWidth - margin, 33, { align: 'right' });
        
        // Spazio dopo logo: 15 (y logo) + 25.3 (altezza logo) + 7mm (0.7cm distacco) = 47.3
        return 47;
      };
      
      const addFooter = (pageNum) => {
        const footerY = pageHeight - 12;
        
        // Linea sottile
        pdf.setDrawColor(...borderGray);
        pdf.setLineWidth(0.3);
        pdf.line(margin, footerY - 3, pageWidth - margin, footerY - 3);
        
        // Testo footer
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(7);
        pdf.setTextColor(...grayText);
        pdf.text('Main Solution S.r.l. - Divisione Engineering & Construction', margin, footerY);
        pdf.text(`Pag. ${pageNum}`, pageWidth / 2, footerY, { align: 'center' });
        pdf.text(formData.data_verbale || new Date().toLocaleDateString('it-IT'), pageWidth - margin, footerY, { align: 'right' });
      };
      
      const checkNewPage = (neededSpace = 30) => {
        if (yPos > pageHeight - neededSpace - 15) {
          addFooter(pdf.getNumberOfPages());
          pdf.addPage();
          yPos = addHeader();
          return true;
        }
        return false;
      };
      
      // ========================================
      // PAGINA 1 - INTESTAZIONE E DATI
      // ========================================
      yPos = addHeader();
      
      // TITOLO PRINCIPALE - COLORE NERO, distanziato 0.7cm dal logo
      const tipoLabel = getTipoVerbaleLabel();
      const titoloVerbale = formData.tipo === 'sopralluogo' 
        ? 'VERBALE DI SOPRALLUOGO'
        : `VERBALE ${tipoLabel.toUpperCase()}`;
      
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(FONT_TITLE);
      pdf.setTextColor(...blackText);  // NERO, non blu
      pdf.text(titoloVerbale, pageWidth / 2, yPos, { align: 'center' });
      yPos += 7;
      
      // Numero verbale
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(FONT_NORMAL);
      pdf.setTextColor(...darkText);
      pdf.text(`N° ${formData.numero_verbale || '___'}`, pageWidth / 2, yPos, { align: 'center' });
      yPos += 5;
      
      // Riferimento normativo
      pdf.setFontSize(FONT_CAPTION);
      pdf.setTextColor(...grayText);
      pdf.text('(ai sensi dell\'art. 92, comma 1, lett. c), D.Lgs. 81/2008)', pageWidth / 2, yPos, { align: 'center' });
      yPos += 10;
      
      // ========================================
      // BOX DATI CANTIERE
      // ========================================
      const boxWidth = pageWidth - 2 * margin;
      const boxStartY = yPos;
      
      // Sfondo box
      pdf.setFillColor(...lightBlue);
      pdf.roundedRect(margin, yPos, boxWidth, 32, 2, 2, 'F');
      
      // Bordo sottile
      pdf.setDrawColor(...mainBlue);
      pdf.setLineWidth(0.3);
      pdf.roundedRect(margin, yPos, boxWidth, 32, 2, 2, 'S');
      
      yPos += 6;
      
      // Titolo sezione
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(FONT_NORMAL);
      pdf.setTextColor(...mainBlue);
      pdf.text('DATI CANTIERE', margin + 5, yPos);
      yPos += 6;
      
      // Dati in due colonne
      const col1X = margin + 5;
      const col2X = margin + boxWidth / 2 + 5;
      
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(FONT_NORMAL);
      pdf.setTextColor(...darkText);
      
      // Colonna 1
      pdf.setFont('helvetica', 'bold');
      pdf.text('Cantiere:', col1X, yPos);
      pdf.setFont('helvetica', 'normal');
      pdf.text(formData.cantiere_nome || 'N/D', col1X + 22, yPos);
      
      // Colonna 2
      pdf.setFont('helvetica', 'bold');
      pdf.text('Data:', col2X, yPos);
      pdf.setFont('helvetica', 'normal');
      const dataFormattata = formData.data_verbale ? new Date(formData.data_verbale).toLocaleDateString('it-IT', { day: '2-digit', month: 'long', year: 'numeric' }) : 'N/D';
      pdf.text(dataFormattata, col2X + 15, yPos);
      yPos += 5;
      
      // Riga 2
      pdf.setFont('helvetica', 'bold');
      pdf.text('PM Cliente:', col1X, yPos);
      pdf.setFont('helvetica', 'normal');
      pdf.text(formData.pm_cliente || 'N/D', col1X + 27, yPos);
      
      pdf.setFont('helvetica', 'bold');
      pdf.text('Orario:', col2X, yPos);
      pdf.setFont('helvetica', 'normal');
      const orarioText = formData.ora_inizio ? `${formData.ora_inizio}${formData.ora_fine ? ' - ' + formData.ora_fine : ''}` : 'N/D';
      pdf.text(orarioText, col2X + 18, yPos);
      yPos += 5;
      
      // Riga 3
      pdf.setFont('helvetica', 'bold');
      pdf.text('Impresa:', col1X, yPos);
      pdf.setFont('helvetica', 'normal');
      pdf.text(formData.impresa_affidataria || 'N/D', col1X + 22, yPos);
      
      if (formData.durata_ore) {
        pdf.setFont('helvetica', 'bold');
        pdf.text('Durata:', col2X, yPos);
        pdf.setFont('helvetica', 'normal');
        pdf.text(`${formData.durata_ore} ore`, col2X + 18, yPos);
      }
      
      yPos = boxStartY + 32 + 8;
      
      // ========================================
      // TESTO INTRODUTTIVO (Giustificato)
      // ========================================
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(FONT_NORMAL);
      pdf.setTextColor(...darkText);
      
      const testoIntro = formData.testo_introduttivo || 
        `Il/La sottoscritto/a, in qualità di ${getTipoVerbaleLabel() === 'Sopralluogo' ? 'Direttore dei Lavori' : 'responsabile'}, per il cantiere di cui all'oggetto, alla presenza di:`;
      
      const introLines = pdf.splitTextToSize(testoIntro, boxWidth);
      introLines.forEach(line => {
        checkNewPage(10);
        pdf.text(line, margin, yPos);
        yPos += 4.5;
      });
      yPos += 4;
      
      // ========================================
      // TABELLA PARTECIPANTI
      // ========================================
      const partecipantiByCategoria = {};
      categoriePartecipanti.forEach(cat => {
        const nomi = partecipantiRows
          .filter(r => r.categoria === cat.value && r.nome)
          .map(r => r.nome);
        if (nomi.length > 0) {
          partecipantiByCategoria[cat.value] = { label: cat.label, value: nomi.join(', ') };
        }
      });
      
      if (Object.keys(partecipantiByCategoria).length > 0) {
        const labelColWidth = 60;
        const valueColWidth = boxWidth - labelColWidth;
        const rowH = 5.5;  // Ridotto per compensare font più piccoli
        
        Object.values(partecipantiByCategoria).forEach((p, idx) => {
          checkNewPage(10);
          
          // Sfondo alternato
          if (idx % 2 === 0) {
            pdf.setFillColor(...lightGray);
            pdf.rect(margin, yPos - 1, boxWidth, rowH, 'F');
          }
          
          // Label
          pdf.setFont('helvetica', 'bold');
          pdf.setFontSize(FONT_NORMAL);
          pdf.setTextColor(...mainBlue);
          pdf.text(p.label, margin + 3, yPos + 3);
          
          // Value
          pdf.setFont('helvetica', 'normal');
          pdf.setTextColor(...darkText);
          pdf.text(p.value, margin + labelColWidth, yPos + 3);
          
          yPos += rowH;
        });
        yPos += 6;
      }
      
      // ========================================
      // SCOPO DELLA RIUNIONE
      // ========================================
      if (formData.scopo_riunione.length > 0) {
        checkNewPage(25);
        
        // Titolo sezione
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(FONT_NORMAL);
        pdf.setTextColor(...mainBlue);
        pdf.text('SCOPO DELLA RIUNIONE', margin, yPos);
        
        // Linea sottile
        pdf.setDrawColor(...mainBlue);
        pdf.setLineWidth(0.5);
        pdf.line(margin, yPos + 2, margin + 55, yPos + 2);
        yPos += 8;
        
        for (const scopo of formData.scopo_riunione) {
          checkNewPage(20);
          
          // Bullet point con scopo
          pdf.setFillColor(...mainBlue);
          pdf.circle(margin + 2, yPos - 1, 1.2, 'F');
          
          const scopoDisplayText = scopo === 'Altro' && formData.scopo_altro_testo 
            ? formData.scopo_altro_testo 
            : scopo;
          
          pdf.setFont('helvetica', 'bold');
          pdf.setFontSize(FONT_NORMAL);
          pdf.setTextColor(...darkText);
          pdf.text(scopoDisplayText, margin + 6, yPos);
          yPos += 5;
          
          // Descrizione dettagliata (giustificata)
          const dettaglio = scopiDettagli[scopo];
          if (dettaglio?.descrizione) {
            pdf.setFont('helvetica', 'normal');
            pdf.setFontSize(FONT_NORMAL);
            pdf.setTextColor(...grayText);
            
            const descLines = pdf.splitTextToSize(dettaglio.descrizione, boxWidth - 10);
            descLines.forEach(line => {
              checkNewPage(8);
              // Testo giustificato (allineato a sinistra per compatibilità jsPDF)
              pdf.text(line, margin + 6, yPos, { align: 'left', maxWidth: boxWidth - 12 });
              yPos += 5;
            });
            yPos += 2;
          }
          
          // Foto - CENTRATE
          if (dettaglio?.foto?.length > 0) {
            for (let i = 0; i < dettaglio.foto.length; i++) {
              checkNewPage(60);
              
              try {
                const imgW = 60;  // Larghezza foto leggermente più grande
                const imgH = 45;  // Altezza proporzionale
                
                // Calcola posizione X per centrare l'immagine
                const imgX = (pageWidth - imgW) / 2;
                
                // Cornice foto centrata
                pdf.setDrawColor(...borderGray);
                pdf.setLineWidth(0.3);
                pdf.rect(imgX, yPos, imgW, imgH, 'S');
                pdf.addImage(dettaglio.foto[i].preview, 'JPEG', imgX, yPos, imgW, imgH);
                
                yPos += imgH + 3;
                
                // Didascalia centrata sotto la foto - FONT 8pt
                pdf.setFont('helvetica', 'normal');
                pdf.setFontSize(FONT_CAPTION);
                pdf.setTextColor(...grayText);
                const captionText = dettaglio.foto[i].descrizione || `Foto ${i + 1}`;
                pdf.text(captionText, pageWidth / 2, yPos, { align: 'center' });
                
                yPos += 8;
              } catch (err) {
                console.error('Errore foto:', err);
              }
            }
          }
          
          yPos += 3;
        }
      }
      
      // ========================================
      // PAGINA FIRME
      // ========================================
      addFooter(pdf.getNumberOfPages());
      pdf.addPage();
      yPos = addHeader();
      
      // Titolo sezione firme
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(FONT_TITLE);
      pdf.setTextColor(...blackText);  // NERO per consistenza con titolo principale
      pdf.text('FIRME DEI PARTECIPANTI', pageWidth / 2, yPos, { align: 'center' });
      yPos += 10;
      
      // Griglia firme 2 colonne
      const firme = [
        { label: 'Per il Committente', sublabel: '(nome e firma)' },
        { label: 'Per l\'Impresa Affidataria', sublabel: '(nome e firma)' },
        { label: 'Coordinatore Sicurezza (CSE)', sublabel: '(nome e firma)' },
        { label: 'Direttore dei Lavori', sublabel: '(nome e firma)' },
        { label: 'Preposto di Cantiere', sublabel: '(nome e firma)' },
        { label: 'Altri Partecipanti', sublabel: '(nome e firma)' }
      ];
      
      const sigRefs = [sigCanvasCommittente, sigCanvasImpresa, sigCanvasCSE, sigCanvasDL, sigCanvasPreposto, sigCanvasSito];
      
      const sigBoxW = (boxWidth - 10) / 2;
      const sigBoxH = 32;
      
      firme.forEach((firma, idx) => {
        const isLeft = idx % 2 === 0;
        const xPos = isLeft ? margin : margin + sigBoxW + 10;
        
        if (idx > 0 && idx % 2 === 0) {
          yPos += sigBoxH + 8;
        }
        
        if (yPos > pageHeight - sigBoxH - 20) {
          addFooter(pdf.getNumberOfPages());
          pdf.addPage();
          yPos = addHeader();
        }
        
        // Box firma con bordo sottile
        pdf.setDrawColor(...borderGray);
        pdf.setLineWidth(0.3);
        pdf.setFillColor(255, 255, 255);
        pdf.roundedRect(xPos, yPos, sigBoxW, sigBoxH, 2, 2, 'FD');
        
        // Label firma
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(FONT_SMALL);
        pdf.setTextColor(...mainBlue);
        pdf.text(firma.label, xPos + 4, yPos + 5);
        
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(FONT_CAPTION);
        pdf.setTextColor(...grayText);
        pdf.text(firma.sublabel, xPos + 4, yPos + 10);
        
        // Linea per firma
        pdf.setDrawColor(...borderGray);
        pdf.setLineWidth(0.2);
        pdf.line(xPos + 4, yPos + sigBoxH - 6, xPos + sigBoxW - 4, yPos + sigBoxH - 6);
        
        // Firma digitale se presente
        const sigRef = sigRefs[idx];
        if (sigRef?.current && !sigRef.current.isEmpty()) {
          try {
            const sigData = sigRef.current.toDataURL();
            pdf.addImage(sigData, 'PNG', xPos + 8, yPos + 12, sigBoxW - 16, 14);
          } catch (e) {}
        }
      });
      
      yPos += sigBoxH + 15;
      
      // Note finali
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(FONT_NORMAL);
      pdf.setTextColor(...grayText);
      pdf.text(`Luogo e data: ${formData.cantiere_nome || '_______________'}, ${dataFormattata}`, margin, yPos);
      
      // Footer ultima pagina
      addFooter(pdf.getNumberOfPages());
      
      console.log('[PDF] Generazione completata, numero pagine:', pdf.getNumberOfPages());

      // Genera il PDF come Blob - usa tipo leggibile per il nome file
      const tipoPerFileName = getTipoVerbaleLabel().replace(/[^a-zA-Z0-9]/g, '_');
      const fileName = `Verbale_${tipoPerFileName}_${formData.numero_verbale || 'NUOVO'}_${formData.data_verbale}.pdf`;
      console.log('[PDF] Nome file:', fileName);
      
      const pdfBlob = pdf.output('blob');
      console.log('[PDF] Blob size:', pdfBlob.size);
      
      const pdfFile = new File([pdfBlob], fileName, { type: 'application/pdf' });
      console.log('[PDF] File creato:', pdfFile.name, pdfFile.size);
      
      // Scarica il PDF localmente
      console.log('[PDF] Salvando file...');
      pdf.save(fileName);
      console.log('[PDF] File salvato!');
      
      // Se c'è onPdfGenerated, passa il file al parent (per mostrarlo nella sezione allegati del form)
      if (onPdfGenerated) {
        onPdfGenerated(pdfFile, fileName);
        toast.success('PDF generato! Clicca "Aggiungi Sopralluogo" per salvare tutto insieme.');
        onOpenChange(false); // Chiudi il dialog
      }
      // Se abbiamo cantiereId ma non onPdfGenerated, carica direttamente negli allegati (vecchio comportamento)
      else if (cantiereId) {
        try {
          // Determina la cartella in base al tipo
          const cartella = formData.tipo === 'sopralluogo' 
            ? 'Verbali di Sopralluogo' 
            : 'Verbali Riunioni di Coordinamento';
          
          const res = await offlineSend({
            method: 'post',
            url: `${API}/cantieri/${cantiereId}/allegati/${encodeURIComponent(cartella)}`,
            file: pdfFile,
            label: `PDF "${fileName}"`
          });
          
          if (res.queued) {
            toast.info('Senza rete: PDF salvato sul telefono, verrà caricato appena torna la connessione');
            return;
          }
          toast.success(`PDF salvato negli allegati (${cartella})`);
          
          // Notifica il parent che il PDF è stato salvato
          if (onPdfSaved) {
            onPdfSaved(fileName, cartella);
          }
        } catch (uploadError) {
          console.error('Errore upload PDF negli allegati:', uploadError);
          toast.warning('PDF scaricato ma non salvato negli allegati');
        }
      } else {
        toast.success('PDF generato con successo!');
      }
      
    } catch (error) {
      console.error('Errore generazione PDF:', error);
      toast.error('Errore nella generazione del PDF');
    } finally {
      setGeneratingPdf(false);
    }
  };

  const handleSave = async () => {
    if (!formData.numero_verbale) {
      toast.error('Inserisci il numero del verbale');
      return;
    }

    const firmeDigitali = {
      firma_sito: sigCanvasSito.current && !sigCanvasSito.current.isEmpty() ? sigCanvasSito.current.toDataURL() : null,
      firma_committente: sigCanvasCommittente.current && !sigCanvasCommittente.current.isEmpty() ? sigCanvasCommittente.current.toDataURL() : null,
      firma_impresa: sigCanvasImpresa.current && !sigCanvasImpresa.current.isEmpty() ? sigCanvasImpresa.current.toDataURL() : null,
      firma_cse: sigCanvasCSE.current && !sigCanvasCSE.current.isEmpty() ? sigCanvasCSE.current.toDataURL() : null,
      firma_dl: sigCanvasDL.current && !sigCanvasDL.current.isEmpty() ? sigCanvasDL.current.toDataURL() : null,
      firma_preposto: sigCanvasPreposto.current && !sigCanvasPreposto.current.isEmpty() ? sigCanvasPreposto.current.toDataURL() : null
    };

    // Genera riepilogo partecipanti
    const partecipantiRiepilogo = partecipantiRows
      .filter(r => r.nome)
      .map(r => {
        const categoria = categoriePartecipanti.find(c => c.value === r.categoria);
        return categoria ? `${categoria.label}: ${r.nome}` : r.nome;
      })
      .join('; ');

    const verbaleData = {
      ...formData,
      partecipanti_riepilogo: partecipantiRiepilogo,
      firme_digitali: firmeDigitali,
      foto: fotoPreview.map(f => ({ preview: f.preview, descrizione: f.descrizione })),
      scopi_dettagli: scopiDettagli,
      verbale_firmato_file: verbaleFirmatoFile // Include il file firmato se presente
    };

    if (onSave) {
      try {
        await onSave(verbaleData);
        // Dopo il salvataggio con successo, abilita la generazione PDF
        setLocalSaved(true);
        // Il toast viene mostrato dal parent (CantiereDetailPage)
      } catch (error) {
        console.error('Errore salvataggio:', error);
        toast.error('Errore nel salvataggio');
      }
    }
    
    // Reset del file firmato dopo il salvataggio
    setVerbaleFirmatoFile(null);
    if (verbaleFirmatoInputRef.current) {
      verbaleFirmatoInputRef.current.value = '';
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg sm:text-xl pr-8" data-testid="verbale-dialog-title">
            <FileText className="w-6 h-6 text-blue-600" />
            {formData.tipo === 'sopralluogo' ? 'Verbale di Sopralluogo' : `Verbale ${getTipoVerbaleLabel()}`}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Tipo e Numero */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <Label>Tipo Verbale</Label>
              <Select 
                value={formData.tipo} 
                onValueChange={(v) => setFormData({...formData, tipo: v, tipo_custom: v === 'altro' ? formData.tipo_custom : ''})}
              >
                <SelectTrigger data-testid="tipo-verbale-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {tipiVerbalePredefiniti.map(tipo => (
                    <SelectItem key={tipo.value} value={tipo.value}>{tipo.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {/* Input per tipo personalizzato quando si seleziona "Altro" */}
              {formData.tipo === 'altro' && (
                <Input 
                  value={formData.tipo_custom}
                  onChange={(e) => setFormData({...formData, tipo_custom: e.target.value})}
                  placeholder="Specifica il tipo di verbale..."
                  className="mt-2"
                  data-testid="tipo-verbale-custom-input"
                />
              )}
            </div>
            <div>
              <Label>Numero Verbale *</Label>
              <Input 
                value={formData.numero_verbale}
                onChange={(e) => setFormData({...formData, numero_verbale: e.target.value})}
                placeholder="Es: 001"
              />
            </div>
            <div>
              <Label>Data</Label>
              <Input 
                type="date"
                value={formData.data_verbale}
                onChange={(e) => setFormData({...formData, data_verbale: e.target.value})}
              />
            </div>
          </div>

          {/* Orari e Durata */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <Label>Ora Inizio</Label>
              <Input 
                type="time"
                value={formData.ora_inizio}
                onChange={(e) => setFormData({...formData, ora_inizio: e.target.value})}
              />
            </div>
            <div>
              <Label>Ora Fine</Label>
              <Input 
                type="time"
                value={formData.ora_fine}
                onChange={(e) => setFormData({...formData, ora_fine: e.target.value})}
              />
            </div>
            <div>
              <Label>Durata (ore)</Label>
              <Input 
                type="number"
                step="0.5"
                value={formData.durata_ore}
                onChange={(e) => setFormData({...formData, durata_ore: e.target.value})}
                placeholder="Es: 2.5"
                className={formData.ora_inizio && formData.ora_fine ? 'bg-green-50 border-green-200' : ''}
              />
              {formData.ora_inizio && formData.ora_fine && formData.durata_ore && (
                <p className="text-xs text-green-600 mt-1">Calcolato automaticamente</p>
              )}
            </div>
          </div>

          {/* Dati Cantiere */}
          <div className="p-4 bg-slate-50 rounded-lg space-y-4">
            <h3 className="font-semibold text-slate-700">Dati Cantiere e Commessa</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <Label>Codice Commessa MS</Label>
                <Input 
                  value={formData.codice_commessa_ms}
                  onChange={(e) => setFormData({...formData, codice_commessa_ms: e.target.value})}
                  placeholder="Es: LEA4458"
                  className="font-mono"
                />
              </div>
              <div>
                <Label>Codice Commessa Cliente</Label>
                <Input 
                  value={formData.codice_commessa_cliente}
                  onChange={(e) => setFormData({...formData, codice_commessa_cliente: e.target.value})}
                  placeholder="Es: EVFV018082022"
                  className="font-mono"
                />
              </div>
              <div>
                <Label>Cantiere</Label>
                <Input 
                  value={formData.cantiere_nome}
                  onChange={(e) => setFormData({...formData, cantiere_nome: e.target.value})}
                />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label>PM Cliente</Label>
                <Input 
                  value={formData.pm_cliente}
                  onChange={(e) => setFormData({...formData, pm_cliente: e.target.value})}
                />
              </div>
              <div>
                <Label>Impresa Affidataria</Label>
                <Input 
                  value={formData.impresa_affidataria}
                  onChange={(e) => setFormData({...formData, impresa_affidataria: e.target.value})}
                />
              </div>
              <div>
                <Label>Impresa Esecutrice</Label>
                <Input 
                  value={formData.impresa_esecutrice}
                  onChange={(e) => setFormData({...formData, impresa_esecutrice: e.target.value})}
                  placeholder="Nome impresa esecutrice..."
                />
              </div>
              <div>
                <Label>Impresa Subappaltatrice</Label>
                <Input 
                  value={formData.impresa_subappaltatrice}
                  onChange={(e) => setFormData({...formData, impresa_subappaltatrice: e.target.value})}
                  placeholder="Nome impresa subappaltatrice..."
                />
              </div>
            </div>
          </div>

          {/* Responsabili con scelta ruolo */}
          <div className="p-4 bg-blue-50 rounded-lg space-y-4">
            <h3 className="font-semibold text-blue-700">Responsabili / Firmatario Verbale</h3>
            
            {/* Scelta ruolo firmatario */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 p-3 bg-blue-100 rounded-lg">
              <div>
                <Label className="text-blue-800">Ruolo Firmatario</Label>
                <Select 
                  value={formData.ruolo_firmatario} 
                  onValueChange={(v) => setFormData({...formData, ruolo_firmatario: v})}
                >
                  <SelectTrigger className="bg-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="direttore_lavori">Direttore dei Lavori</SelectItem>
                    <SelectItem value="assistente_dl">Assistente del Direttore dei Lavori</SelectItem>
                    <SelectItem value="assistente_cantiere">Assistente di Cantiere</SelectItem>
                    <SelectItem value="cse">Coordinatore in fase di Esecuzione (CSE)</SelectItem>
                    <SelectItem value="csp">Coordinatore in fase di Progettazione (CSP)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-blue-800">Genere</Label>
                <Select 
                  value={formData.genere_firmatario} 
                  onValueChange={(v) => setFormData({...formData, genere_firmatario: v})}
                >
                  <SelectTrigger className="bg-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="M">Maschile (Il sottoscritto)</SelectItem>
                    <SelectItem value="F">Femminile (La sottoscritta)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-blue-800">N° Ordine Ingegneri</Label>
                <Input 
                  value={formData.direttore_lavori_ordine}
                  onChange={(e) => setFormData({...formData, direttore_lavori_ordine: e.target.value})}
                  placeholder="Es: 2799"
                  className="bg-white"
                />
              </div>
            </div>
            
            {/* Nome firmatario - dropdown + input editabile */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <Label>Firmatario (Nome e Cognome)</Label>
                <div className="flex flex-col sm:flex-row gap-2">
                  <Select 
                    value={formData.direttore_lavori_id} 
                    onValueChange={(v) => {
                      if (v === 'manual') {
                        setFormData({...formData, direttore_lavori_id: ''});
                      } else {
                        const user = users.find(u => u.id === v);
                        const figura = figureProfessionaliCantiere.find(f => f.nome_completo === v);
                        if (user) {
                          setFormData({
                            ...formData, 
                            direttore_lavori_id: v,
                            direttore_lavori_nome: `${user.nome} ${user.cognome || ''}`.trim()
                          });
                        } else if (figura) {
                          setFormData({
                            ...formData, 
                            direttore_lavori_id: v,
                            direttore_lavori_nome: figura.nome_completo
                          });
                        }
                      }
                    }}
                  >
                    <SelectTrigger className="w-1/2">
                      <SelectValue placeholder="Seleziona o inserisci manualmente..." />
                    </SelectTrigger>
                    <SelectContent>
                      {/* Figure professionali del cantiere */}
                      {figureProfessionaliCantiere.filter(f => ['dl', 'assistente_cantiere', 'cse'].includes(f.ruolo)).map((f, idx) => (
                        <SelectItem key={`fig-${idx}`} value={f.nome_completo}>
                          {f.nome_completo} ({f.ruolo?.toUpperCase()})
                        </SelectItem>
                      ))}
                      {/* Utenti sistema */}
                      {users.slice(0, 10).map(u => (
                        <SelectItem key={u.id} value={u.id}>
                          {u.nome} {u.cognome || ''}
                        </SelectItem>
                      ))}
                      <SelectItem value="manual" className="text-blue-600 font-medium">
                        + Inserisci manualmente
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <Input 
                    value={formData.direttore_lavori_nome}
                    onChange={(e) => setFormData({...formData, direttore_lavori_nome: e.target.value})}
                    placeholder="Nome e cognome firmatario..."
                    className="flex-1"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Testo Introduttivo Editabile */}
          <div className="p-4 bg-indigo-50 rounded-lg space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h3 className="font-semibold text-indigo-700">Testo Introduttivo del Verbale</h3>
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => improveTextWithAI('testo_introduttivo', formData.testo_introduttivo)}
                  disabled={aiLoading.testo_introduttivo}
                  className="text-indigo-600 border-indigo-300 hover:bg-indigo-100"
                >
                  {aiLoading.testo_introduttivo ? (
                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  ) : (
                    <Sparkles className="w-4 h-4 mr-1" />
                  )}
                  Migliora con AI
                </Button>
                <span className="text-xs text-indigo-500 bg-indigo-100 px-2 py-1 rounded">Personalizzabile</span>
              </div>
            </div>
            <p className="text-xs text-indigo-600">Questo testo apparirà nel PDF prima della tabella partecipanti. Puoi modificarlo liberamente o usare l'AI per migliorarlo.</p>
            <Textarea 
              value={formData.testo_introduttivo}
              onChange={(e) => setFormData({...formData, testo_introduttivo: e.target.value})}
              placeholder="Es: Il sottoscritto Ing. [Nome] in qualità di Direttore dei Lavori..."
              rows={3}
              className="bg-white text-sm"
            />
          </div>

          {/* Partecipanti - Righe Dinamiche */}
          <div className="p-4 bg-green-50 rounded-lg space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-green-700">Partecipanti alla Riunione</h3>
                <p className="text-xs text-green-600">Aggiungi righe per ogni partecipante. Usa il dropdown per selezionare la categoria e il nome.</p>
              </div>
              <Button
                type="button"
                size="sm"
                onClick={addPartecipanteRow}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                <Plus className="w-4 h-4 mr-1" />
                Aggiungi Riga
              </Button>
            </div>
            
            {/* Intestazione tabella */}
            <div className="hidden sm:grid grid-cols-12 gap-2 text-xs font-semibold text-green-800 border-b border-green-200 pb-2">
              <div className="sm:col-span-4">Categoria</div>
              <div className="sm:col-span-4">Seleziona da Figure</div>
              <div className="sm:col-span-3">Nome (editabile)</div>
              <div className="sm:col-span-1"></div>
            </div>
            
            {/* Righe dinamiche */}
            {partecipantiRows.map((row, idx) => (
              <div key={row.id} className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-center p-2 bg-white rounded border border-green-100">
                {/* Dropdown Categoria */}
                <div className="sm:col-span-4">
                  <Select 
                    value={row.categoria} 
                    onValueChange={(v) => updatePartecipanteRow(row.id, 'categoria', v)}
                  >
                    <SelectTrigger className="text-sm">
                      <SelectValue placeholder="Seleziona categoria..." />
                    </SelectTrigger>
                    <SelectContent>
                      {categoriePartecipanti.map(cat => (
                        <SelectItem key={cat.value} value={cat.value}>
                          {cat.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Dropdown Figure Professionali */}
                <div className="sm:col-span-4">
                  <Select 
                    onValueChange={(v) => {
                      if (v !== 'manual') {
                        updatePartecipanteRow(row.id, 'nome', v);
                      }
                    }}
                    disabled={!row.categoria}
                  >
                    <SelectTrigger className="text-sm">
                      <SelectValue placeholder={row.categoria ? "Seleziona figura..." : "Prima scegli categoria"} />
                    </SelectTrigger>
                    <SelectContent>
                      {figureProfessionaliCantiere.length > 0 ? (
                        figureProfessionaliCantiere.map((f, fIdx) => (
                          <SelectItem key={fIdx} value={f.nome_completo}>
                            {f.nome_completo} ({f.ruolo?.toUpperCase() || 'N/D'})
                          </SelectItem>
                        ))
                      ) : (
                        <SelectItem value="none" disabled>Nessuna figura assegnata</SelectItem>
                      )}
                      <SelectItem value="manual" className="text-blue-600 font-medium">
                        + Inserisci manualmente
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Input Nome (editabile) */}
                <div className="sm:col-span-3">
                  <Input 
                    value={row.nome}
                    onChange={(e) => updatePartecipanteRow(row.id, 'nome', e.target.value)}
                    placeholder="Nome e cognome..."
                    className="text-sm"
                  />
                </div>
                
                {/* Pulsante Rimuovi */}
                <div className="sm:col-span-1 flex justify-center">
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => removePartecipanteRow(row.id)}
                    disabled={partecipantiRows.length === 1}
                    className="text-red-500 hover:text-red-700 hover:bg-red-50 p-1"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
            
            {/* Riepilogo per categoria */}
            {partecipantiRows.filter(r => r.nome).length > 0 && (
              <div className="mt-4 p-3 bg-green-100 rounded text-sm">
                <div className="font-medium text-green-800 mb-2">Riepilogo Partecipanti:</div>
                {categoriePartecipanti.map(cat => {
                  const nomi = partecipantiRows.filter(r => r.categoria === cat.value && r.nome).map(r => r.nome);
                  if (nomi.length === 0) return null;
                  return (
                    <div key={cat.value} className="text-green-700">
                      <span className="font-medium">{cat.label}:</span> {nomi.join(', ')}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Scopo della Riunione */}
          <div className="p-4 bg-amber-50 rounded-lg space-y-4">
            <h3 className="font-semibold text-amber-700">Scopo della Riunione</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {scopiPredefiniti.map(scopo => (
                <div key={scopo} className="flex items-center gap-2">
                  <Checkbox 
                    id={scopo}
                    checked={formData.scopo_riunione.includes(scopo)}
                    onCheckedChange={(checked) => handleScopoChange(scopo, checked)}
                  />
                  <Label htmlFor={scopo} className="text-sm cursor-pointer">{scopo}</Label>
                </div>
              ))}
            </div>
            
            {/* Campo per specificare "Altro" */}
            {formData.scopo_riunione.includes('Altro') && (
              <div className="mt-3 p-3 bg-white rounded-lg border border-amber-200">
                <Label className="text-sm text-amber-700 mb-2 block">Specifica lo scopo "Altro":</Label>
                <Input 
                  value={formData.scopo_altro_testo}
                  onChange={(e) => setFormData({...formData, scopo_altro_testo: e.target.value})}
                  placeholder="Es: Verifica impianti elettrici, Collaudo strutture, etc."
                  className="text-sm"
                />
              </div>
            )}
            
            {/* Dettagli per ogni scopo selezionato */}
            {formData.scopo_riunione.length > 0 && (
              <div className="mt-4 space-y-4">
                {formData.scopo_riunione.map((scopo, idx) => (
                  <div key={scopo} className="p-4 bg-white rounded-lg border border-amber-200 shadow-sm">
                    <h4 className="font-medium text-amber-800 mb-3 flex items-center gap-2">
                      <span className="w-6 h-6 bg-amber-600 text-white rounded-full flex items-center justify-center text-xs">{idx + 1}</span>
                      {scopo === 'Altro' && formData.scopo_altro_testo 
                        ? `Altro: ${formData.scopo_altro_testo}` 
                        : scopo}
                    </h4>
                    
                    {/* Campo descrizione attività */}
                    <div className="mb-3">
                      <div className="flex items-center justify-between mb-1">
                        <Label className="text-sm text-amber-700">Attività svolta / Descrizione</Label>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => improveTextWithAI(`scopo_${scopo}`, scopiDettagli[scopo]?.descrizione || '', `descrizione attività per "${scopo === 'Altro' ? formData.scopo_altro_testo || 'Altro' : scopo}"`)}
                          disabled={aiLoading[`scopo_${scopo}`] || !scopiDettagli[scopo]?.descrizione}
                          className="text-amber-600 border-amber-300 hover:bg-amber-100 h-7 text-xs"
                        >
                          {aiLoading[`scopo_${scopo}`] ? (
                            <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                          ) : (
                            <Sparkles className="w-3 h-3 mr-1" />
                          )}
                          Migliora con AI
                        </Button>
                      </div>
                      <Textarea 
                        value={scopiDettagli[scopo]?.descrizione || ''}
                        onChange={(e) => updateScopoDescrizione(scopo, e.target.value)}
                        placeholder={`Descrivi l'attività svolta per: ${scopo}...`}
                        rows={3}
                        className="text-sm"
                      />
                    </div>
                    
                    {/* Upload foto per questo scopo */}
                    <div className="mt-3">
                      <Label className="text-sm text-amber-700 flex items-center gap-1">
                        <Upload className="w-4 h-4" />
                        Foto relative a questo scopo
                      </Label>
                      <Input 
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={(e) => handleScopoFotoUpload(scopo, e)}
                        className="mt-1 cursor-pointer text-sm"
                      />
                      
                      {/* Preview foto */}
                      {scopiDettagli[scopo]?.foto?.length > 0 && (
                        <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 mt-2">
                          {scopiDettagli[scopo].foto.map((foto, fotoIdx) => (
                            <div key={fotoIdx} className="relative border rounded p-1 bg-slate-50">
                              <img 
                                src={foto.preview} 
                                alt={`Foto ${fotoIdx + 1}`}
                                className="w-full h-16 object-cover rounded"
                              />
                              <Input 
                                placeholder="Didascalia..."
                                value={foto.descrizione}
                                onChange={(e) => updateScopoFotoDescrizione(scopo, fotoIdx, e.target.value)}
                                className="mt-1 text-xs h-6 p-1"
                              />
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => removeScopoFoto(scopo, fotoIdx)}
                                className="absolute -top-1 -right-1 h-5 w-5 p-0 bg-red-500 hover:bg-red-600 text-white rounded-full"
                              >
                                <X className="w-3 h-3" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Tipo Firma - Checkbox */}
          <div className="p-4 bg-slate-100 rounded-lg space-y-4">
            <h3 className="font-semibold text-slate-700 flex items-center gap-2">
              <Edit className="w-5 h-5" />
              Modalità Firma
            </h3>
            
            {/* Selezione tipo firma */}
            <div className="flex items-center gap-6 p-3 bg-white rounded-lg border">
              <label className="flex items-center gap-2 cursor-pointer">
                <input 
                  type="radio" 
                  name="tipoFirma" 
                  value="tradizionale"
                  checked={formData.tipo_firma === 'tradizionale'}
                  onChange={() => setFormData({...formData, tipo_firma: 'tradizionale'})}
                  className="w-4 h-4 text-blue-600"
                />
                <span className="text-sm font-medium">Firma tradizionale (su carta)</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input 
                  type="radio" 
                  name="tipoFirma" 
                  value="digitale"
                  checked={formData.tipo_firma === 'digitale'}
                  onChange={() => setFormData({...formData, tipo_firma: 'digitale'})}
                  className="w-4 h-4 text-blue-600"
                />
                <span className="text-sm font-medium">Firma digitale</span>
              </label>
            </div>
            
            {formData.tipo_firma === 'tradizionale' ? (
              <p className="text-sm text-slate-500 bg-slate-50 p-3 rounded">
                📝 Il PDF verrà generato con spazi vuoti per le firme. Stampa il documento e raccogli le firme manualmente.
              </p>
            ) : (
              <>
                <p className="text-xs text-slate-500">
                  Firma qui digitalmente. Le firme verranno inserite direttamente nel PDF.
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {[
                    { label: 'Per il Sito', ref: sigCanvasSito },
                    { label: 'Per il Committente', ref: sigCanvasCommittente },
                    { label: 'Per l\'Impresa Affidataria', ref: sigCanvasImpresa },
                    { label: 'Per il CSE', ref: sigCanvasCSE },
                    { label: 'Direttore dei Lavori', ref: sigCanvasDL },
                    { label: 'Il Preposto', ref: sigCanvasPreposto }
                  ].map((firma, idx) => (
                    <div key={idx} className="space-y-2">
                      <Label className="text-sm">{firma.label}</Label>
                      <div className="border border-slate-300 rounded bg-white">
                        <SignatureCanvas 
                          ref={firma.ref}
                          penColor="black"
                          canvasProps={{ width: 250, height: 80, className: 'signature-canvas' }}
                        />
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => clearSignature(firma.ref)}>
                        <Trash2 className="w-3 h-3 mr-1" /> Cancella
                      </Button>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Azioni */}
          <div className="flex flex-wrap justify-between gap-2 pt-4 border-t">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Annulla
            </Button>
            <div className="flex flex-wrap gap-2">
              <Button 
                onClick={generatePDF}
                disabled={generatingPdf || !canGeneratePdf}
                variant="outline"
                className={`border-blue-600 text-blue-600 hover:bg-blue-50 ${!canGeneratePdf ? 'opacity-50 cursor-not-allowed' : ''}`}
                data-testid="genera-pdf-btn"
                title={!canGeneratePdf ? 'Prima salva il sopralluogo' : 'Genera PDF del verbale'}
              >
                <FileText className="w-4 h-4 mr-2" />
                {generatingPdf ? 'Generando...' : 'Genera PDF'}
              </Button>
              <Button 
                onClick={handleSave}
                className="bg-green-600 hover:bg-green-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                {sopralluogoEsistente ? 'Salva Modifiche' : (canGeneratePdf ? 'Salvato ✓' : 'Salva Sopralluogo')}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default VerbaleFormDialog;
